import { storeToRefs } from 'pinia';
import { useWebflowStore } from '@/stores/webflow-store';
import {
  Author,
  Category,
  LocationWebflow,
  Post,
  VisiblePost,
} from '@/interfaces/webflow/webflow.interface';
import { useElasticStore } from '@/stores/elastic-store';
import { ref, watch } from 'vue';
import { i18n } from '@/plugins/i18n';

export const useWebflow = () => {
  const { getCollections } = useWebflowStore();
  const { postsData, mergedData, todayPosts } = storeToRefs(useWebflowStore());
  const { filters } = storeToRefs(useElasticStore());
  const internalGuideLoading = ref(false);

  const handleGetCollections = async () => {
    if (localStorage.getItem('posts')) {
      mergedData.value = JSON.parse(localStorage.getItem('posts') as string);

      verifyTodayPosts();
    } else {
      await getCollectionsData();
    }
  };

  const getFilteredCategoriesPosts = (
    category: string | null,
    country: string | null,
  ): VisiblePost[] => {
    if (mergedData.value) {
      let posts = mergedData.value ?? [];
      if (category) {
        posts = posts.filter(
          (post) => post['main-category'] && post['main-category'].id === category,
        );
      }

      if (country) {
        posts = posts.filter(
          (post) =>
            post.locations &&
            post.locations.some((location) => location && location.name === country),
        );
      }

      return posts.length > 0
        ? posts
        : getFilteredCategoriesPosts('6647233b91387caf6916870e', null);
    }

    return [];
  };

  const getCategory = () => {
    const categoryIds = {
      professional: '6644cdfa10e4a5592dd3763b',
      student: '66618ee9465dd45146a5d9e1',
    };

    if (localStorage.getItem('auth')) {
      const user = JSON.parse(localStorage.getItem('auth') as string).user;
      if (user) {
        const category =
          user.userType === 'student' || user.userType === 'professional'
            ? user.userType
            : 'student';
        return categoryIds[`${category as keyof typeof categoryIds}`];
      }
    }

    return categoryIds[`student`];
  };

  const getCountry = () => {
    return filters.value.country ?? null;
  };

  const verifyTodayPosts = () => {
    let init = 0;
    let end = 15;
    const country = getCountry();
    const category = getCategory();
    const posts = getFilteredCategoriesPosts(category, country);
    const today = new Date();

    if (localStorage.getItem('last-stored-posts')) {
      const lastStoredPosts = JSON.parse(localStorage.getItem('last-stored-posts') as string);

      const storedDate = new Date(lastStoredPosts.today);

      const [year, month, day] = [
        storedDate.getFullYear(),
        storedDate.getMonth(),
        storedDate.getDate(),
      ];
      const [yearToday, monthToday, dayToday] = [
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
      ];

      const isSameDay = year === yearToday && month === monthToday && day === dayToday;
      const isPossibleSlice = posts.length > lastStoredPosts.end + 15;

      if (!isSameDay && isPossibleSlice) {
        init = lastStoredPosts.init + 15;
        end = lastStoredPosts.end + 15;
      }
    }

    setTodayPost(today, init, end, category as string, country, posts!);
  };

  const setTodayPost = (
    date: Date,
    init: number,
    end: number,
    category: string,
    country: string,
    posts: VisiblePost[],
  ) => {
    todayPosts.value = posts.slice(init, end);

    const lastStoredPosts = {
      today: date,
      init: init,
      end: end,
      category: category,
      country: country,
    };
    localStorage.setItem('last-stored-posts', JSON.stringify(lastStoredPosts));
  };

  const getCollectionsData = async () => {
    try {
      internalGuideLoading.value = true;
      await getCollections('posts');
      await getCollections('authors');
      await getCollections('locations');
      await getCollections('categories');

      if (
        postsData.value.posts &&
        postsData.value.authors &&
        postsData.value.locations &&
        postsData.value.categories
      ) {
        mergedData.value = await mergeRelatedData(
          postsData.value.posts,
          postsData.value.authors,
          postsData.value.locations,
          postsData.value.categories,
        );

        localStorage.setItem('posts', JSON.stringify(mergedData.value));
        await verifyTodayPosts();
      }
    } catch (error) {
      console.error(error);
    } finally {
      internalGuideLoading.value = false;
    }
  };

  const mergeRelatedData = (
    posts: Post[],
    authors: Author[],
    locations: LocationWebflow[],
    categories: Category[],
  ) => {
    const authorsMap = Object.fromEntries(authors.map((author) => [author.id, author]));
    const locationsMap = Object.fromEntries(locations.map((location) => [location.id, location]));
    const categoriesMap = Object.fromEntries(categories.map((category) => [category.id, category]));

    return posts.map((post) => {
      return {
        ...post,
        author: authorsMap[post.author],
        locations: post.locations?.map((locationId) => locationsMap[locationId]),
        'main-category': categoriesMap[post['main-category']],
      };
    });
  };

  watch(filters.value, () => {
    verifyTodayPosts();
  });

  watch(i18n.global.locale, () => {
    getCollectionsData();
  });

  return { internalGuideLoading, handleGetCollections, getFilteredCategoriesPosts, getCategory };
};
